import React, { useState } from 'react';

const WHATSAPP_NUMBER = '447859151818';
const DEFAULT_MSG = 'Hello! I am interested in learning more about your courses at Atlas Academy.';

const WhatsAppIcon = ({ size = 24 }) => (
  <svg width={size} height={size} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export default function WhatsAppChat() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MSG);

  const handleSend = () => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {open && (
        <div
          className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-80 overflow-hidden"
          style={{ animation: 'fadeSlideUp 0.2s ease-out' }}
        >
          {/* Header */}
          <div className="bg-[#25d366] px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <WhatsAppIcon size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm leading-tight">Atlas Academy</p>
              <p className="text-white/80 text-xs">Typically replies within minutes</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white transition-colors text-xl leading-none p-1"
              aria-label="Close WhatsApp chat"
            >
              ×
            </button>
          </div>

          {/* Chat body */}
          <div className="p-4 bg-[#e5ddd5]">
            <div className="bg-white rounded-xl rounded-tl-none p-3 shadow-sm text-sm text-gray-700 leading-relaxed mb-3 max-w-[90%]">
              👋 Hi there! How can we help you? Send us a message and we'll reply on WhatsApp shortly.
            </div>
          </div>

          {/* Input area */}
          <div className="p-4 bg-white border-t border-gray-100">
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={3}
              className="w-full border border-gray-200 rounded-lg p-3 text-sm text-gray-700 resize-none focus:outline-none focus:border-[#25d366] focus:ring-1 focus:ring-[#25d366]"
              placeholder="Type your message..."
            />
            <button
              onClick={handleSend}
              className="w-full mt-2 bg-[#25d366] hover:bg-[#1ebe5d] text-white font-semibold py-2.5 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
            >
              <WhatsAppIcon size={16} />
              Chat on WhatsApp
            </button>
            <a
              href="tel:+442033555646"
              className="w-full mt-2 border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium py-2 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
            >
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Call +44 (0) 20 3355 5646
            </a>
          </div>
        </div>
      )}

      {/* Floating action button */}
      <button
        onClick={() => setOpen(!open)}
        className="w-14 h-14 bg-[#25d366] hover:bg-[#1ebe5d] rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 relative"
        aria-label="Chat with us on WhatsApp"
      >
        {open ? (
          <svg width="22" height="22" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <WhatsAppIcon size={28} />
        )}
        {!open && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold leading-none">1</span>
        )}
      </button>
    </div>
  );
}
